import { css } from '@compiled/react';

import { wacToken } from '@/libraries/design/foundations';

// link colors matching the link component's anchor styles
const linkColors = {
	defaultColor: wacToken('color.extended.blue700'),
	hoverColor: wacToken('color.primary.black'),
	visitedColor: wacToken('color.extended.purple800'),
	visitedHoverColor: wacToken('color.primary.black'),
};

const linkStyle = css({
	// base anchor link styles from link component
	alignItems: 'center',
	display: 'flex',
	color: linkColors.defaultColor,
	textDecoration: 'none',
	lineHeight: '1.4',
	fontSize: '1rem',
	fontFamily: wacToken('font.charlie.display'),
	cursor: 'pointer',
	paddingTop: '8px',
	paddingBottom: '8px',

	'&:hover, &:focus': {
		color: linkColors.hoverColor,
		textDecoration: 'underline',
		textShadow: 'none',
	},

	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.extended.blue600')}`,
		outlineOffset: '2px',
	},

	'&:visited': {
		color: linkColors.visitedColor,
	},

	'&:visited:hover, &:visited:focus': {
		color: linkColors.visitedHoverColor,
		textDecoration: 'underline',
	},
});

// <option> is semantically correct, but can only be used inside <select>
// since this is used in a custom dropdown (ContentBlock), <div role="option"> is used
export const Option = ({
	children,
	onClick,
	isSelected = false,
	...props
}: {
	children: React.ReactNode;
	onClick: (event: React.MouseEvent<HTMLElement>) => void;
	isSelected?: boolean;
}) => {
	return (
		<div
			role="option"
			tabIndex={0}
			css={linkStyle}
			onClick={onClick}
			onKeyDown={(e) => {
				// keyboard navigation for accessibility
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					// create a synthetic mouse event for consistency
					const syntheticEvent = {
						...e,
						currentTarget: e.currentTarget,
						target: e.target,
					} as unknown as React.MouseEvent<HTMLElement>;
					onClick(syntheticEvent);
				}
			}}
			aria-selected={isSelected}
			{...props}
		>
			{children}
		</div>
	);
};
